import {
  CreatePixelConfigRequest,
  CreatePixelConfigRequestSchema,
  UpdatePixelConfigRequest,
  UpdatePixelConfigRequestSchema,
  PixelConfigResponse,
  PixelConfigResponseSchema,
  ListPixelConfigsResponse,
  ListPixelConfigsResponseSchema,
  PixelTestResponse,
  PixelTestResponseSchema,
} from "@codway-dev/shared";
import { AuthService } from "./auth.service";

const API_BASE_URL = "/api";

async function authHeaders(): Promise<HeadersInit> {
  const token = await AuthService.getShopifyToken();
  if (!token) throw new Error("No Shopify token available");
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

/** Extracts a human message from the backend error envelope ({ error: { message } }). */
async function errorMessage(
  response: Response,
  fallback: string
): Promise<string> {
  const body = await response.json().catch(() => null);
  return (
    body?.error?.message ||
    body?.message ||
    `${fallback}: ${response.statusText}`
  );
}

export const PixelService = {
  list: async (): Promise<ListPixelConfigsResponse> => {
    const response = await fetch(`${API_BASE_URL}/admin/pixels`, {
      method: "GET",
      headers: await authHeaders(),
    });
    if (!response.ok) {
      throw new Error(await errorMessage(response, "Failed to load pixels"));
    }
    return ListPixelConfigsResponseSchema.parse(await response.json());
  },

  create: async (
    body: CreatePixelConfigRequest
  ): Promise<PixelConfigResponse> => {
    const valid = CreatePixelConfigRequestSchema.parse(body);
    const response = await fetch(`${API_BASE_URL}/admin/pixels`, {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify(valid),
    });
    if (!response.ok) {
      throw new Error(await errorMessage(response, "Failed to create pixel"));
    }
    return PixelConfigResponseSchema.parse(await response.json());
  },

  update: async (
    id: string,
    body: UpdatePixelConfigRequest
  ): Promise<PixelConfigResponse> => {
    const valid = UpdatePixelConfigRequestSchema.parse(body);
    const response = await fetch(`${API_BASE_URL}/admin/pixels/${id}`, {
      method: "PUT",
      headers: await authHeaders(),
      body: JSON.stringify(valid),
    });
    if (!response.ok) {
      throw new Error(await errorMessage(response, "Failed to update pixel"));
    }
    return PixelConfigResponseSchema.parse(await response.json());
  },

  // Server-side leg of the "Probar" button: fires a test event from the backend
  // and returns the provider's accept/reject. A provider rejection comes back as
  // a 200 with ok:false (read result.message); a 4xx means an invalid target.
  test: async (id: string): Promise<PixelTestResponse> => {
    const response = await fetch(`${API_BASE_URL}/admin/pixels/${id}/test`, {
      method: "POST",
      headers: await authHeaders(),
      // authHeaders sets Content-Type: application/json, and Fastify rejects an
      // empty JSON body — so send an explicit (empty) object even though the
      // route takes no input.
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      throw new Error(await errorMessage(response, "Failed to test pixel"));
    }
    return PixelTestResponseSchema.parse(await response.json());
  },
};
